import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, BellRing, CheckCheck, AlertTriangle, AlertOctagon, Info,
  Radio, TrendingUp, Zap, Settings2, ExternalLink,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import AlertRulesDialog from "@/components/AlertRulesDialog";

/**
 * Bell icon + notification tray. Lives in the shared TopBar so it shows in
 * both SWING and INTRADAY modes.
 *
 * Polling: only when the tab is visible. `document.visibilitychange` gates the
 * 30s interval to avoid wasting API calls / battery when the tab is backgrounded.
 */

const SEV_STYLE = {
  info:     { cls: "border-cyan-500/50 text-cyan-300 bg-cyan-500/10",     Icon: Info },
  warning:  { cls: "border-amber-500/50 text-amber-300 bg-amber-500/10",  Icon: AlertTriangle },
  critical: { cls: "border-red-500/60 text-red-300 bg-red-500/10",        Icon: AlertOctagon },
};

const TYPE_ICON = {
  signal_change:  TrendingUp,
  verdict_change: Zap,
  regime_change:  Radio,
  invalidation:   AlertOctagon,
  rule_fire:      Bell,
};

function fmtRel(ms) {
  if (!ms) return "";
  const delta = Math.max(0, Date.now() - ms);
  const s = Math.floor(delta / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationTray() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const fetchUnread = useCallback(async () => {
    try {
      const r = await api.get("/alerts/unread_count");
      setUnread(r.data?.unread ?? 0);
    } catch { /* ignore transient errors */ }
  }, []);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/alerts", { params: { limit: 50 } });
      setAlerts(r.data?.alerts || []);
      const u = (r.data?.alerts || []).filter((a) => !a.read).length;
      setUnread(u);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  // Poll every 30s while visible. Pause when hidden.
  useEffect(() => {
    let id = null;
    const start = () => {
      fetchUnread();
      id = setInterval(fetchUnread, 30_000);
    };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => (document.visibilityState === "visible" ? start() : stop());
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [fetchUnread]);

  useEffect(() => { if (open) fetchAlerts(); }, [open, fetchAlerts]);

  const markRead = async (id) => {
    try {
      await api.post(`/alerts/mark_read/${id}`);
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
      setUnread((n) => Math.max(0, n - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/alerts/mark_all_read");
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const handleClick = async (a) => {
    if (!a.read) await markRead(a.id);
    if (a.link) {
      setOpen(false);
      nav(a.link);
    }
  };

  const BellIcon = unread > 0 ? BellRing : Bell;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="notif-tray-toggle"
            data-unread={unread}
            className="relative h-8 w-8 flex items-center justify-center border border-[#1E293B] hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-colors"
            aria-label={unread > 0 ? `${unread} unread alerts` : "Notifications"}
          >
            <BellIcon className={`h-3.5 w-3.5 ${unread > 0 ? "text-amber-300" : "text-slate-400"}`} />
            {unread > 0 && (
              <span
                data-testid="notif-tray-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 border border-red-500/70 bg-red-500/20 text-red-200 mono text-[9px] font-bold uppercase tracking-widest tabular-nums flex items-center justify-center"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          data-testid="notif-tray-content"
          side="bottom"
          align="end"
          sideOffset={8}
          className="w-[360px] p-0 border border-[#1E293B] bg-[#0B111C] shadow-2xl"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#1E293B] bg-[#080B11]">
            <div className="flex items-center gap-2">
              <Bell className="h-3 w-3 text-cyan-400" />
              <span className="mono text-[10px] uppercase tracking-widest text-slate-300 font-semibold">
                Notifications
              </span>
              {unread > 0 && (
                <span className="mono text-[10px] px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300 uppercase tracking-widest">
                  {unread} unread
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="notif-manage-rules"
                onClick={() => setRulesOpen(true)}
                title="Manage alert rules"
                className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[#1E293B] text-slate-400 hover:text-slate-100 hover:border-cyan-500/40 flex items-center gap-1"
              >
                <Settings2 className="h-2.5 w-2.5" /> Rules
              </button>
              <button
                data-testid="notif-mark-all-read"
                onClick={markAllRead}
                disabled={unread === 0}
                className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[#1E293B] text-slate-400 hover:text-slate-100 hover:border-cyan-500/40 disabled:opacity-40 flex items-center gap-1"
              >
                <CheckCheck className="h-2.5 w-2.5" /> Mark all
              </button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-[#1E293B]">
            {loading && alerts.length === 0 && (
              <div className="p-6 text-center mono text-[11px] text-slate-500">Loading…</div>
            )}
            {!loading && alerts.length === 0 && (
              <div data-testid="notif-tray-empty" className="p-6 text-center mono text-[11px] text-slate-500">
                No notifications yet.
              </div>
            )}
            {alerts.map((a) => {
              const sev = SEV_STYLE[a.severity] || SEV_STYLE.info;
              const TypeIcon = TYPE_ICON[a.type] || sev.Icon;
              return (
                <button
                  key={a.id}
                  data-testid={`notif-item-${a.id}`}
                  data-read={a.read ? "true" : "false"}
                  data-severity={a.severity}
                  onClick={() => handleClick(a)}
                  className={`w-full text-left px-3 py-2.5 flex gap-3 hover:bg-[#151C2C] transition-colors ${a.read ? "opacity-60" : ""}`}
                >
                  <div className={`h-7 w-7 shrink-0 flex items-center justify-center border ${sev.cls}`}>
                    <TypeIcon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <span className="mono text-[11px] font-bold text-slate-100 truncate">{a.title}</span>
                      {!a.read && (
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0 mt-1.5" aria-hidden />
                      )}
                    </div>
                    <div className="mono text-[10px] text-slate-400 mt-0.5 line-clamp-2">{a.body}</div>
                    <div className="mono text-[9px] uppercase tracking-widest text-slate-500 mt-1 flex items-center gap-2">
                      <span>{fmtRel(a.created_at_ms)}</span>
                      <span className="text-slate-600">·</span>
                      <span className={sev.cls.split(" ").find((c) => c.startsWith("text-"))}>{a.severity}</span>
                      {a.link && <ExternalLink className="h-2.5 w-2.5 text-slate-600 ml-auto" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <AlertRulesDialog
        open={rulesOpen}
        onOpenChange={setRulesOpen}
        onChanged={() => { fetchUnread(); if (open) fetchAlerts(); }}
      />
    </>
  );
}
